import React, { useEffect, useMemo, useState } from "react";
import { formatDate } from "../../utils/dateFormat";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import DeleteIcon from "@material-ui/icons/Delete";
import RefreshIcon from "@material-ui/icons/Refresh";

import EnquiryService from "./Locality/Service/enquiryService";
import SchoolService from "./Locality/Service/schoolService";
import { showAppConfirm } from '../../context/AlertContext';

const useStyles = makeStyles((theme) => ({
  page: {
    padding: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(3),
    },
  },
  card: {
    borderRadius: 14,
    padding: theme.spacing(2),
    boxShadow: "0 10px 30px rgba(18, 38, 63, 0.08)",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: {
    fontWeight: 900,
  },
  subTitle: {
    color: "rgba(0,0,0,0.65)",
    marginTop: theme.spacing(0.5),
  },
  linkBox: {
    marginTop: theme.spacing(2),
    border: "1px solid rgba(0,0,0,0.10)",
    borderRadius: 12,
    padding: theme.spacing(1.25),
    background: "#fafbff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  linkText: {
    fontFamily: "monospace",
    fontSize: 13,
    wordBreak: "break-all",
  },
  tableWrap: {
    marginTop: theme.spacing(2),
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 12,
    overflowX: "auto",
  },
  headCell: {
    fontWeight: 900,
    background: "#f3f6ff",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    whiteSpace: "nowrap",
  },
  empty: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: "rgba(0,0,0,0.65)",
  },
  danger: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
  fieldRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    maxWidth: 560,
  },
  fieldActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    flexWrap: "wrap",
  },
}));

const MAX_CUSTOM_FIELDS = 5;

/** Value a submission gave for a custom field ("-" when empty or not asked yet). */
function customAnswer(row, key) {
  const hit = (row?.customFields || []).find((f) => f?.key === key);
  const value = String(hit?.value || "").trim();
  return value || "-";
}

function formatDDMMYYYY(date) {
  return formatDate(date) || "-";
}

function normalizeDob(dob) {
  return formatDate(dob) || "-";
}

function formatAcademicYear(row) {
  const label = String(row?.academicYear || row?.academicYearId?.acadamicYear || "").trim();
  return label || "-";
}

function safeUserDetails() {
  try {
    return JSON.parse(localStorage.getItem("userDetail") || "{}");
  } catch (e) {
    return {};
  }
}

export default function EnquiryAdmin() {
  const classes = useStyles();

  const userDetails = useMemo(() => safeUserDetails(), []);
  const schooleId = userDetails?.schooleId || userDetails?.schoolId;

  const [schoolName, setSchoolName] = useState("");
  const [shareLink, setShareLink] = useState("");

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "" });

  // Optional custom fields shown on the public form (up to 5).
  const [customFields, setCustomFields] = useState([]);
  const [savedCustomFields, setSavedCustomFields] = useState([]);
  const [savingFields, setSavingFields] = useState(false);

  const makeSlug = (name) =>
    String(name || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  useEffect(() => {
    const origin = window.location.origin;
    if (schooleId) {
      const slug = makeSlug(schoolName) || "school";
      setShareLink(`${origin}/#/montessori/enquiry/${schooleId}/${slug}`);
    } else {
      setShareLink("");
    }
  }, [schooleId, schoolName]);

  useEffect(() => {
    let mounted = true;
    if (!schooleId) return undefined;

    SchoolService.getSchooleRegistrationById(schooleId)
      .then((res) => {
        if (!mounted) return;
        setSchoolName(res?.schooleName || "");
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [schooleId]);

  const load = async () => {
    if (!schooleId) return;
    try {
      setIsLoading(true);
      setError("");
      const res = await EnquiryService.listEnquiriesForSchool(schooleId);
      setItems(Array.isArray(res) ? res : []);
    } catch (e) {
      setError(e?.message || "Unable to load enquiries.");
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schooleId]);

  useEffect(() => {
    if (!schooleId) return undefined;
    let mounted = true;
    EnquiryService.getFormConfig(schooleId)
      .then((res) => {
        if (!mounted) return;
        const fields = Array.isArray(res?.customFields) ? res.customFields : [];
        setSavedCustomFields(fields);
        setCustomFields(fields.map((f) => ({ key: f.key, label: f.label })));
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [schooleId]);

  const addCustomField = () => {
    setCustomFields((prev) => (prev.length >= MAX_CUSTOM_FIELDS ? prev : [...prev, { key: "", label: "" }]));
  };

  const removeCustomField = (index) => {
    setCustomFields((prev) => prev.filter((_, i) => i !== index));
  };

  const updateCustomFieldLabel = (index, label) => {
    setCustomFields((prev) => prev.map((f, i) => (i === index ? { ...f, label } : f)));
  };

  const saveCustomFields = async () => {
    if (!schooleId) return;
    try {
      setSavingFields(true);
      const res = await EnquiryService.saveFormConfig(
        schooleId,
        customFields.map((f) => ({ key: f.key || undefined, label: String(f.label || "").trim() }))
      );
      const fields = Array.isArray(res?.customFields) ? res.customFields : [];
      setSavedCustomFields(fields);
      setCustomFields(fields.map((f) => ({ key: f.key, label: f.label })));
      setSnack({ open: true, message: "Custom fields saved" });
    } catch (e) {
      setSnack({ open: true, message: e?.response?.data?.message || e?.message || "Failed to save custom fields" });
    } finally {
      setSavingFields(false);
    }
  };

  const customFieldsChanged =
    JSON.stringify(customFields.map((f) => [f.key || "", String(f.label || "").trim()])) !==
    JSON.stringify(savedCustomFields.map((f) => [f.key, f.label]));

  // Table columns: the current custom fields, plus fields that were removed
  // later but still have answers in older submissions.
  const customColumns = useMemo(() => {
    const cols = savedCustomFields.map((f) => ({ key: f.key, label: f.label }));
    const known = new Set(cols.map((c) => c.key));
    items.forEach((row) => {
      (row?.customFields || []).forEach((f) => {
        if (f?.key && !known.has(f.key)) {
          known.add(f.key);
          cols.push({ key: f.key, label: f.label || "Custom field" });
        }
      });
    });
    return cols;
  }, [savedCustomFields, items]);

  const handleDelete = async (row) => {
    const enquiryId = row?._id;
    if (!schooleId || !enquiryId) return;
    const name = row?.studentName || "this enquiry";
    if (!await showAppConfirm(`Delete enquiry for "${name}"?`)) return;

    try {
      setDeletingId(String(enquiryId));
      await EnquiryService.deleteEnquiry(schooleId, enquiryId);
      setItems((prev) => prev.filter((item) => String(item._id) !== String(enquiryId)));
      setSnack({ open: true, message: "Enquiry deleted" });
    } catch (e) {
      setSnack({ open: true, message: e?.message || "Failed to delete enquiry" });
    } finally {
      setDeletingId("");
    }
  };

  const copyLink = async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setSnack({ open: true, message: "Link copied" });
    } catch (e) {
      setSnack({ open: true, message: "Copy failed. Please copy manually." });
    }
  };

  return (
    <div className={classes.page}>
      <Card className={classes.card}>
        <div className={classes.headerRow}>
          <Box>
            <Typography variant="h5" className={classes.title}>
              Enquiry Form
            </Typography>
            <Typography variant="body2" className={classes.subTitle}>
              Generate a public link for parents and view submitted enquiries
              {schoolName ? ` • ${schoolName}` : ""}
            </Typography>
          </Box>

          <Box display="flex" gridGap={8}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading || !schooleId}>
              Refresh
            </Button>
          </Box>
        </div>

        {!schooleId ? (
          <Box mt={2}>
            <Typography className={classes.danger}>
              Your account does not have a school linked. Please login as a School Admin to use this feature.
            </Typography>
          </Box>
        ) : (
          <>
            <div className={classes.linkBox}>
              <Box>
                <Typography variant="subtitle2" style={{ fontWeight: 900 }}>
                  Shareable link (public)
                </Typography>
                <Typography
                  component="a"
                  href={shareLink}
                  target="_blank"
                  rel="noreferrer"
                  className={classes.linkText}
                  style={{ color: "#1976d2", textDecoration: "underline" }}
                >
                  {shareLink}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gridGap={8}>
                <IconButton onClick={copyLink} aria-label="copy link" disabled={!shareLink}>
                  <FileCopyIcon />
                </IconButton>
              </Box>
            </div>

            <Divider style={{ margin: "18px 0" }} />

            <Typography variant="h6" style={{ fontWeight: 900 }}>
              Custom form fields
            </Typography>
            <Typography variant="body2" className={classes.subTitle}>
              Add up to {MAX_CUSTOM_FIELDS} extra text fields to the public form. Parents can leave them empty.
            </Typography>

            {customFields.map((field, index) => (
              <div className={classes.fieldRow} key={field.key || `new-${index}`}>
                <TextField
                  label={`Custom field ${index + 1}`}
                  placeholder="e.g. Previous school"
                  value={field.label}
                  onChange={(e) => updateCustomFieldLabel(index, e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                />
                <IconButton
                  size="small"
                  aria-label={`Remove custom field ${index + 1}`}
                  onClick={() => removeCustomField(index)}
                  style={{ backgroundColor: "#ffebee" }}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </div>
            ))}

            <div className={classes.fieldActions}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addCustomField}
                disabled={customFields.length >= MAX_CUSTOM_FIELDS}
              >
                Add field ({customFields.length}/{MAX_CUSTOM_FIELDS})
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={saveCustomFields}
                disabled={savingFields || !customFieldsChanged}
              >
                {savingFields ? "Saving..." : "Save fields"}
              </Button>
            </div>

            <Divider style={{ margin: "18px 0" }} />

            <Typography variant="h6" style={{ fontWeight: 900 }}>
              Submissions
            </Typography>

            {error ? (
              <Typography className={classes.danger} style={{ marginTop: 10 }}>
                {error}
              </Typography>
            ) : null}

            {isLoading ? (
              <Box mt={2} display="flex" alignItems="center" gridGap={10}>
                <CircularProgress size={20} />
                <Typography>Loading...</Typography>
              </Box>
            ) : (
              <div className={classes.tableWrap}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headCell}>Date</TableCell>
                      <TableCell className={classes.headCell}>Academic Year</TableCell>
                      <TableCell className={classes.headCell}>Student Name</TableCell>
                      <TableCell className={classes.headCell}>DOB</TableCell>
                      <TableCell className={classes.headCell}>Gender</TableCell>
                      <TableCell className={classes.headCell}>Father name</TableCell>
                      <TableCell className={classes.headCell}>Father contact</TableCell>
                      <TableCell className={classes.headCell}>Mother name</TableCell>
                      <TableCell className={classes.headCell}>Mother contact</TableCell>
                      <TableCell className={classes.headCell}>Email</TableCell>
                      <TableCell className={classes.headCell}>Class/Age group</TableCell>
                      <TableCell className={classes.headCell}>Admission sought for</TableCell>
                      <TableCell className={classes.headCell}>Additional information</TableCell>
                      {customColumns.length > 0 && (
                        <TableCell className={classes.headCell}>Custom fields</TableCell>
                      )}
                      <TableCell className={classes.headCell} align="center">
                        Delete
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={14 + (customColumns.length > 0 ? 1 : 0)} className={classes.empty}>
                          No enquiries submitted yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((row) => (
                        <TableRow key={row?._id}>
                          <TableCell className={classes.bodyCell}>
                            {row?.createdAt ? formatDDMMYYYY(new Date(row.createdAt)) : "-"}
                          </TableCell>
                          <TableCell className={classes.bodyCell}>{formatAcademicYear(row)}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.studentName || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{normalizeDob(row?.dob)}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.gender || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.fatherName || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.fatherContactNumber || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.motherName || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.motherContactNumber || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.email || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.classOrAgeGroup || "-"}</TableCell>
                          <TableCell className={classes.bodyCell}>{row?.admissionSoughtFor || "-"}</TableCell>
                          <TableCell style={{ minWidth: 220 }}>
                            <Typography variant="body2" style={{ whiteSpace: "normal" }}>
                              {row?.additionalInformation || "-"}
                            </Typography>
                          </TableCell>
                          {customColumns.length > 0 && (
                            <TableCell style={{ minWidth: 240 }}>
                              {/* Every custom field, one per line: "Label: answer" ("-" when empty). */}
                              {customColumns.map((col) => (
                                <Typography
                                  key={col.key}
                                  variant="body2"
                                  style={{ whiteSpace: "normal", lineHeight: 1.6 }}
                                >
                                  <span style={{ fontWeight: 700 }}>{col.label}:</span>{" "}
                                  {customAnswer(row, col.key)}
                                </Typography>
                              ))}
                            </TableCell>
                          )}
                          <TableCell className={classes.bodyCell} align="center">
                            <IconButton
                              size="small"
                              aria-label="delete enquiry"
                              onClick={() => handleDelete(row)}
                              disabled={Boolean(deletingId)}
                            >
                              {deletingId === String(row._id) ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DeleteIcon fontSize="small" color="error" />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack({ open: false, message: "" })}
        message={snack.message}
      />
    </div>
  );
}

